import api from './api';

/**
 * One order field as the registry describes it: the DB column the form writes, and
 * whether Change All is switched on for it.
 *
 * Keyed by the camelCase VIEW KEY the form addresses the field by — the registry's
 * `view_key` column, which exists precisely because the two are not derivable from one
 * another (`serviceFailure` lives in `dvirframe`, `idoNumber` in `freightowner`).
 */
export interface ChangeAllOrderField {
  column: string;
  changeAll: boolean;
}

export type ChangeAllOrderFields = Record<string, ChangeAllOrderField>;

// Global (one answer for every order) and changed only by an admin in Data Management,
// so it is fetched once and the promise cached for the app's lifetime — same shape as
// fetchOrderFieldDisplayRules. Provider-independent so it works everywhere the order
// form renders: the /pro page, the dispatch drawer, isolated test trees.
let fieldsPromise: Promise<ChangeAllOrderFields | null> | null = null;

/**
 * The Change All field registry, or `null` when the server cannot answer.
 *
 * `null` is a real answer and means "use your built-in set": the backend returns it
 * when migration 467 has not been applied to that database, and we return it on a
 * network failure. Both cases must leave Change All working exactly as it did before
 * the registry existed rather than closing every checkbox — migrations here are
 * hand-applied per environment, so a frontend genuinely can run ahead of one.
 */
export function fetchChangeAllOrderFields(): Promise<ChangeAllOrderFields | null> {
  if (!fieldsPromise) {
    fieldsPromise = api
      .get<{ fields: ChangeAllOrderFields | null }>('/orders/change-all/order-fields')
      .then((res) => res.data?.fields ?? null)
      .catch((err) => {
        // Reset so a later mount can retry, and fall back to the built-in set.
        fieldsPromise = null;
        console.error('Failed to load Change All field registry:', err);
        return null;
      });
  }
  return fieldsPromise;
}

/** Test/HMR aid — drop the cache so the next fetch re-hits the API. */
export function _resetChangeAllOrderFieldsCache(): void {
  fieldsPromise = null;
}
